import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DynamoConnector } from '../../../data/DynamoConnector';
import { useDeepLinkParam } from '../../../utils/deepLink/useDeepLinkSegment';
import {
    ActionButton,
    Flex,
    Item,
    Menu,
    MenuTrigger,
    Picker,
    Section,
    Checkbox,
    ProgressCircle,
} from '@adobe/react-spectrum';
import ChevronDown from '@spectrum-icons/workflow/ChevronDown';
import DownloadFromCloud from '@spectrum-icons/workflow/DownloadFromCloud';
import Data from '@spectrum-icons/workflow/Data';
import Bookmark from '@spectrum-icons/workflow/Bookmark';
import DataMapping from '@spectrum-icons/workflow/DataMapping';
import ColorPalette from '@spectrum-icons/workflow/ColorPalette';
import HotFixes from '@spectrum-icons/workflow/HotFixes';
import FlashOn from '@spectrum-icons/workflow/FlashOn';

import { fetchUpgradesListByCategory, fetchUpgrades, resetUpgrades } from '../../../data/redux/slices/upgradesSlice';
import { fetchCommanderStatsHealth } from '../../../data/redux/slices/commanderCardStatsSlice';
import { ARCHETYPE_ANY, prettyStatsMajor, prettyStatsSub } from '../../../data/statsArchetypeTaxonomy';
import { ManaSelector } from '../../filterpanel/ManaSelector';
import { useDebouncedCallback } from 'use-debounce';
import { SearedLoader } from '../../genericLoadingDialog/SearedLoader';
import { useOpenCardInfo } from '../../common/cardInfoDialog/useOpenCardInfo';
import { FilterBanner } from '../../common/cardStats/FilterBanner';
import {
    UpgradelizerCardTile,
    UpgradelizerComboRow,
    UpgradelizerSynergyRow,
} from './upgradelizer/UpgradelizerRenderers';
import {
    UpgradelizerAdvancedFilters,
    ADVANCED_FILTER_DEFAULTS,
    advancedFiltersToParams,
    countActiveAdvancedFilters,
} from './upgradelizer/UpgradelizerAdvancedFilters';
import { UpgradelizerMatchDetailDialog } from './upgradelizer/UpgradelizerMatchDetailDialog';
import { UPGRADES_BASE_SEARCH_OVERHAUL_ENABLED } from './upgradelizer/upgradesFeatureFlags';
import isMobile from 'ismobilejs';
import HeroPanel, { HeroPanelTotal } from '../../common/heroPanel/HeroPanel';
import './UpgradelizerPane.css';

// ---- Constants ----

const ROLE_CATEGORY_CONSTANTS = {
    COUNTERSPELL: { enum: 'COUNTERSPELL', label: 'Counterspells', field: 'counterspell' },
    CANTRIP: { enum: 'CANTRIP', label: 'Draw', field: 'cantrip' },
    RAMP: { enum: 'RAMP', label: 'Ramp', field: 'ramp' },
    STAX: { enum: 'STAX', label: 'Stax', field: 'stax' },
    TUTOR: { enum: 'TUTOR', label: 'Tutors', field: 'tutor' },
    FASTMANA: { enum: 'FASTMANA', label: 'Fastmana', field: 'fastmana' },
    TAXES: { enum: 'TAXES', label: 'Taxes', field: 'taxes' },
    GRAVEYARD_HATE: { enum: 'GRAVEYARD_HATE', label: 'Graveyard Hate', field: 'graveyard' },
    SPOT_REMOVAL: { enum: 'SPOT_REMOVAL', label: 'Spot Removal', field: 'spotRemoval' },
    BOARD_WIPES: { enum: 'BOARD_WIPES', label: 'Board Wipes', field: 'boardWipes' },
    EVASION: { enum: 'EVASION', label: 'Evasion', field: 'evasion' },
    OTHER_CONTROL: { enum: 'OTHER_CONTROL', label: 'Other Control', field: 'otherControl' },
    DISCARD: { enum: 'DISCARD', label: 'Discard', field: 'discard' },
    RECURSION: { enum: 'RECURSION', label: 'Recursion', field: 'recursion' },
    TOPDECK: { enum: 'TOPDECK', label: 'Top Deck', field: 'topdeck' },
    COSTREDUCTION: { enum: 'COSTREDUCTION', label: 'Cost Reduction', field: 'costReduction' },
    MULTIPLIERS: { enum: 'MULTIPLIERS', label: 'Multipliers', field: 'multipliers' },
    OVERRUN: { enum: 'OVERRUN', label: 'Overrun', field: 'overrun' },
    TOKENS: { enum: 'TOKENS', label: 'Token Generators', field: 'tokens' },
    GROUPSLUG: { enum: 'GROUPSLUG', label: 'Group Slug', field: 'groupslug' },
    BURN: { enum: 'BURN', label: 'Burn', field: 'burn' },
};

const ROLE_CATEGORY_SECTIONS = {
    CONSISTENCY: {
        label: 'Consistency',
        enum: 'CONSISTENCY',
        items: ['TUTOR', 'RECURSION', 'TOPDECK', 'CANTRIP', 'MULTIPLIERS'],
    },
    EFFICIENCY: {
        label: 'Efficiency',
        enum: 'EFFICIENCY',
        items: ['FASTMANA', 'RAMP', 'COSTREDUCTION'],
    },
    INTERACTION: {
        label: 'Interaction',
        enum: 'INTERACTION',
        items: [
            'SPOT_REMOVAL',
            'BOARD_WIPES',
            'COUNTERSPELL',
            'STAX',
            'TAXES',
            'GRAVEYARD_HATE',
            'EVASION',
            'OTHER_CONTROL',
            'DISCARD',
        ],
    },
    WINCONS: {
        label: 'Wincons',
        enum: 'WINCONS',
        items: ['OVERRUN', 'GROUPSLUG', 'BURN', 'TOKENS'],
    },
};

const BASE_CARD_TYPE_LIST = [
    'Artifact',
    'Battle',
    'Creature',
    'Enchantment',
    'Instant',
    'Land',
    'Planeswalker',
    'Sorcery',
];

const BIN_SECTIONS = [
    {
        id: 'brackets',
        name: 'Bracket',
        children: [
            { key: 'bracket_1', label: 'Bracket 1' },
            { key: 'bracket_2', label: 'Bracket 2' },
            { key: 'bracket_3', label: 'Bracket 3' },
            { key: 'bracket_4', label: 'Bracket 4' },
            { key: 'bracket_5', label: 'Bracket 5' },
        ],
    },
    {
        id: 'power',
        name: 'Power Level',
        children: [
            { key: 'power_1_3', label: 'Power 1–3' },
            { key: 'power_3_5', label: 'Power 3–5' },
            { key: 'power_5_7', label: 'Power 5–7' },
            { key: 'power_7_9', label: 'Power 7–9' },
            { key: 'power_9_10', label: 'Power 9–10' },
        ],
    },
];

const BIN_LABEL_BY_KEY = BIN_SECTIONS.reduce((acc, section) => {
    section.children.forEach((child) => {
        acc[child.key] = child.label;
    });
    return acc;
}, {});

const binKeyToParams = (binKey) => {
    if (!binKey) return {};
    if (binKey.startsWith('bracket_')) {
        const n = parseInt(binKey.slice('bracket_'.length), 10);
        return Number.isFinite(n) ? { bracket: n } : {};
    }
    if (binKey.startsWith('power_')) {
        return { power: binKey.slice('power_'.length) };
    }
    return {};
};

const SYNERGY_VARIANTS = [
    { id: 'synergy_supporters', label: 'Supporters', hint: 'Cards feeding the commander' },
    { id: 'synergy_enablers', label: 'Enablers', hint: 'Cards unlocking the commander' },
    { id: 'synergy_triggers', label: 'Triggers', hint: 'Commander trigger interactions' },
    { id: 'synergy_anchors', label: 'Anchors', hint: 'Deck-wide synergy backbones' },
    { id: 'top_synergies', label: 'Top patterns', hint: 'Synergy hooks to build around' },
];

// Spectrum Menu/Picker keys can't be empty strings — use a sentinel for
// "any/unset" options and translate to/from '' at the onSelectionChange
// boundary so the API payload never sees the sentinel.
const MANABASE_ANY_KEY = '__any__';

const MANABASE_PRODUCER_TYPES = [
    { id: '', label: 'Any producer' },
    { id: 'land', label: 'Land' },
    { id: 'artifact', label: 'Artifact' },
    { id: 'creature', label: 'Creature' },
    { id: 'enchantment', label: 'Enchantment' },
    { id: 'instant', label: 'Instant' },
    { id: 'sorcery', label: 'Sorcery' },
    { id: 'other', label: 'Other' },
];

const MANABASE_LAND_TYPES = [
    { id: '', label: 'Any land type' },
    { id: 'basic', label: 'Basic' },
    { id: 'rainbow', label: 'Rainbow / WUBRG' },
    { id: 'multi', label: 'Multicolor' },
    { id: 'monoColor', label: 'Mono-color' },
    { id: 'colorless', label: 'Colorless' },
    { id: 'fetch', label: 'Fetch' },
    { id: 'utility', label: 'Utility' },
    { id: 'other', label: 'Other' },
];

// Archetype labels come from the shared statsArchetypeTaxonomy module. The
// option lists themselves are driven from the per-bin availability tree on
// the health response (only majors/subs with actual data for the current
// commander+bin appear), not a static taxonomy.
const prettyMinor = prettyStatsSub;

const fmtBinCount = (n) => (typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString() : '—');

const COMBO_EFFECTS = [
    { id: 'infinite-mana', label: 'Infinite mana' },
    { id: 'infinite-draw', label: 'Infinite draw' },
    { id: 'infinite-turns', label: 'Infinite turns' },
    { id: 'infinite-tokens', label: 'Infinite tokens' },
    { id: 'infinite-damage', label: 'Infinite damage' },
    { id: 'infinite-storm', label: 'Infinite storm' },
    { id: 'win-game', label: 'Win the game' },
    { id: 'opponents-locked', label: 'Opponents locked' },
];

const MODES = [
    {
        id: 'role',
        label: 'By Role',
        icon: Data,
        hint: 'Format-wide category search',
        group: 'generic',
    },
    {
        id: 'staples',
        label: 'Staples',
        icon: Bookmark,
        hint: 'Most-played cards at this bin',
        group: 'tuned',
    },
    {
        id: 'synergy',
        label: 'Synergy',
        icon: DataMapping,
        hint: 'Synergy hooks',
        group: 'tuned',
    },
    {
        id: 'manabase',
        label: 'Manabase',
        icon: ColorPalette,
        hint: 'Role-filtered manabase',
        group: 'tuned',
    },
    {
        id: 'archetype',
        label: 'Archetype',
        icon: HotFixes,
        hint: 'Specific archetype path',
        group: 'tuned',
    },
    {
        id: 'combos',
        label: 'Combos',
        icon: FlashOn,
        hint: 'Near-miss and full combos',
        group: 'tuned',
    },
];

const GENERIC_MODES = MODES.filter((m) => m.group === 'generic');
const TUNED_MODES = MODES.filter((m) => m.group === 'tuned');

// ---- Helpers ----

const sortByName = (a, b) => {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
};

const getRoleCategoryList = () =>
    Object.keys(ROLE_CATEGORY_SECTIONS).map((key) => {
        const section = ROLE_CATEGORY_SECTIONS[key];
        return {
            name: section.label,
            id: section.enum,
            children: (section.items || [])
                .slice()
                .sort(sortByName)
                .map((id) => ({
                    id,
                    name: ROLE_CATEGORY_CONSTANTS[id]?.label || '',
                })),
        };
    });

const roleCategoryByEnum = Object.values(ROLE_CATEGORY_CONSTANTS).reduce((acc, c) => {
    acc[c.enum] = c;
    return acc;
}, {});

const commonCardTypeList = BASE_CARD_TYPE_LIST.map((item) => ({
    name: item,
    id: item.toUpperCase(),
}))
    .slice()
    .sort(sortByName);

const asColorSet = (str) =>
    new Set(
        String(str || '')
            .toUpperCase()
            .split('')
            .filter((c) => 'WUBRG'.includes(c))
    );

const renderModeTab = (m, mode, setMode, disabled = false) => {
    const Icon = m.icon;
    const active = mode === m.id;
    return (
        <button
            key={m.id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-disabled={disabled}
            disabled={disabled}
            className={`upgradelizerModeTab${active ? ' is-active' : ''}${disabled ? ' is-disabled' : ''}`}
            onClick={() => {
                if (!disabled) setMode(m.id);
            }}
        >
            <Icon size="XS" />
            <span className="upgradelizerModeLabel">{m.label}</span>
            <span className="upgradelizerModeHint">{m.hint}</span>
        </button>
    );
};

const renderTunedCaption = ({ health, healthFetching, tunedAvailable, availableBinSections }) => {
    if (!health && healthFetching) return 'Checking commander stats availability…';
    if (!health) return 'Commander-specific · pulled from aggregate stats';
    const status = health.status;
    if (status === 'pending') {
        return 'Gathering stats for this commander — check back soon';
    }
    if (status === 'below_threshold') {
        const threshold = health.threshold || 50;
        const deckCount = health.deckCount || 0;
        return `Not enough decks yet — need ${threshold}, have ${deckCount}`;
    }
    if (status === 'disabled') {
        return 'Temporarily unavailable';
    }
    if (tunedAvailable === false) {
        return 'No populated bins yet for this commander';
    }
    const totalBins = availableBinSections.reduce((acc, s) => acc + s.children.length, 0);
    const suffix = status === 'stale' ? ' · updating' : '';
    return `${totalBins} bin${totalBins === 1 ? '' : 's'} available · pulled from aggregate stats${suffix}`;
};

// ---- Main component ----

export function UpgradelizerPane() {
    const dispatch = useDispatch();
    const deck = useSelector((state) => state.deck.deck);

    // Card-category (role) state.
    const legacyList = useSelector((state) => state.upgrades.list);
    const legacyNextCursor = useSelector((state) => state.upgrades.nextCursor);
    const legacyIsFetching = useSelector((state) => state.upgrades.isFetching);
    const legacyHasDoneFirstLoad = useSelector((state) => state.upgrades.hasDoneFirstLoad);
    const legacyTotal = useSelector((state) => state.upgrades.legacyTotal);
    const legacyError = useSelector((state) => state.upgrades.legacyError);
    const legacyErrorKind = useSelector((state) => state.upgrades.legacyErrorKind);
    const legacyRetryAfter = useSelector((state) => state.upgrades.legacyRetryAfter);

    // Source-based state.
    const sourceItems = useSelector((state) => state.upgrades.items);
    const sourceCursor = useSelector((state) => state.upgrades.cursor);
    const sourceTotal = useSelector((state) => state.upgrades.total);
    const sourceStatus = useSelector((state) => state.upgrades.status);
    const sourceNote = useSelector((state) => state.upgrades.note);
    const sourceLastSource = useSelector((state) => state.upgrades.source);
    const sourceIsFetching = useSelector((state) => state.upgrades.isFetchingSource);
    const sourceHasDoneFirstLoad = useSelector((state) => state.upgrades.hasDoneFirstSourceLoad);
    const sourceError = useSelector((state) => state.upgrades.error);

    // Commander stats health — tells us which bins have data and whether
    // commander-tuned modes can run at all.
    const commanderId = deck?.commanderIndexId;
    const health = useSelector((state) => (commanderId ? state.commanderCardStats.healthById[commanderId] : null));
    const healthFetching = useSelector((state) =>
        commanderId ? Boolean(state.commanderCardStats.healthFetching[commanderId]) : false
    );

    const openCardInfo = useOpenCardInfo();
    const mobile = isMobile(window.navigator).phone;

    const filteredLegacyList = (legacyList || []).filter((item) => item && item?.id);

    const commanderNames = useMemo(() => {
        const raw = deck?.commanders;
        if (Array.isArray(raw)) return raw.filter(Boolean);
        if (typeof raw === 'string' && raw.length > 0) return [raw];
        return [];
    }, [deck?.commanders]);
    const commanderDisplay = commanderNames.length > 0 ? commanderNames.join(' & ') : 'this commander';

    // ---- Live pricing for visible upgrade tiles ----
    // Scryfall bulk lookup populates this map (lowercased card name →
    // { usd, purchaseUris }) as items come in. The ref mirrors state so
    // we can decide "do I already have this card?" without depending on
    // the cache in the effect's deps (which would loop). State drives
    // re-render when new prices arrive.
    const priceCacheRef = useRef({});
    const [priceByName, setPriceByName] = useState({});

    // ---- Shared filter state ----
    const [mode, setMode] = useDeepLinkParam('mode', 'role');
    const deckColorDefault = deck?.colorIdentity?.toLowerCase() || null;
    const [colorFilters, setColorFilters] = useDeepLinkParam('colors', deckColorDefault);
    const [selectedTypes, setSelectedTypes] = useState(new Set(BASE_CARD_TYPE_LIST.map((item) => item.toUpperCase())));

    const deckBracket = Math.max(1, Math.min(5, Math.round(parseFloat(deck?.bracketRating || 4)) || 4));
    const [binKey, setBinKey] = useDeepLinkParam('bin', `bracket_${deckBracket}`);
    const activeBinLabel = BIN_LABEL_BY_KEY[binKey] || 'Bracket';

    // ---- Health-driven availability ----
    // Fetch the health probe once per commander — tells us which bins have
    // data and whether commander-tuned modes are runnable at all.
    useEffect(() => {
        if (commanderId) dispatch(fetchCommanderStatsHealth(commanderId));
    }, [commanderId, dispatch]);

    // Bulk-fetch live USD prices + TCGPlayer URLs for every card name in
    // the current upgrade results (both legacy and source modes, plus
    // combo members and synergy anchors). Only requests names we don't
    // already have cached this session — paginating in more rows just
    // adds the new names on top.
    useEffect(() => {
        const names = new Set();
        (legacyList || []).forEach((item) => {
            if (item?.name) names.add(item.name);
        });
        (sourceItems || []).forEach((item) => {
            if (item?.name) names.add(item.name);
            (item?.cards || []).forEach((c) => {
                if (c?.name) names.add(c.name);
            });
            if (item?.anchorCard?.name) names.add(item.anchorCard.name);
        });
        const missing = Array.from(names).filter((n) => !priceCacheRef.current[n.toLowerCase()]);
        if (missing.length === 0) return;
        let cancelled = false;
        DynamoConnector.getScryfallCollection(missing, (result) => {
            if (cancelled) return;
            // Write an entry for every requested name — real data when
            // Scryfall returned something, a `notFound` sentinel for
            // anything it didn't. Without the sentinel, tiles for
            // unknown cards would stay in skeleton state forever
            // because the consumer can't distinguish "still loading"
            // from "loaded but missing".
            const merged = { ...priceCacheRef.current };
            const safe = result || {};
            missing.forEach((name) => {
                const key = name.toLowerCase();
                if (safe[key]) {
                    merged[key] = safe[key];
                } else {
                    merged[key] = { usd: null, purchaseUris: {}, notFound: true };
                }
            });
            priceCacheRef.current = merged;
            setPriceByName(merged);
        });
        return () => {
            cancelled = true;
        };
    }, [legacyList, sourceItems]);

    const availableBins = health?.availableBins || {};
    const tunedAvailable = useMemo(() => {
        // Ready/stale + at least one non-null bin with deckCount > 0 means
        // the tuned group is usable. below_threshold / pending / disabled
        // all mean "no tuned data yet".
        if (!health) return null; // not loaded yet — optimistic
        const status = health.status;
        if (status === 'below_threshold' || status === 'disabled') return false;
        if (status === 'pending') return false;
        const hasAny = Object.values(availableBins).some((b) => b && (b.deckCount ?? 0) > 0);
        return hasAny;
    }, [health, availableBins]);

    const availableBinSections = useMemo(() => {
        // If health hasn't loaded, show the full list (optimistic). Once it
        // lands, filter to only bins that reported non-null with >0 decks.
        if (!health) return BIN_SECTIONS;
        return BIN_SECTIONS.map((section) => ({
            ...section,
            children: section.children
                .filter((child) => {
                    const info = availableBins[child.key];
                    return info && (info.deckCount ?? 0) > 0;
                })
                .map((child) => {
                    const info = availableBins[child.key];
                    const count = info?.deckCount ?? 0;
                    return { ...child, deckCount: count };
                }),
        })).filter((section) => section.children.length > 0);
    }, [health, availableBins]);

    const availableBinKeys = useMemo(() => {
        const keys = new Set();
        availableBinSections.forEach((s) => s.children.forEach((c) => keys.add(c.key)));
        return keys;
    }, [availableBinSections]);

    // If current binKey isn't in the available set once health lands, snap
    // to the nearest valid bin so we don't fire a request for an empty bin.
    useEffect(() => {
        if (!health) return;
        if (availableBinKeys.size === 0) return;
        if (availableBinKeys.has(binKey)) return;
        // Prefer a bracket bin closest to the deck's own bracket; else any.
        const preferred = [`bracket_${deckBracket}`, 'bracket_4', 'bracket_5', 'bracket_3', 'bracket_2', 'bracket_1'];
        const pick = preferred.find((k) => availableBinKeys.has(k)) || availableBinSections[0]?.children[0]?.key;
        if (pick) setBinKey(pick);
    }, [health, availableBinKeys, binKey, deckBracket, availableBinSections]);

    // If tuned group becomes unavailable while user is on a tuned tab, snap
    // back to 'role' so we don't keep firing requests that can't succeed.
    useEffect(() => {
        if (tunedAvailable === false && mode !== 'role') {
            setMode('role');
        }
    }, [tunedAvailable, mode]);

    // ---- Role mode state ----
    const [roleCategoryId, setRoleCategoryId] = useDeepLinkParam('category', null);
    const [reservedListSelected, setReservedListSelected] = useState(false);
    // Advanced filters for the card_category (role) mode — exposes every
    // additive filter on the base-search overhaul (qualifiers, oracle
    // full-text, effect/action/zone/flag pickers, CMC/price ranges,
    // combo-completion, matchesPerCard, etc.). Held as a single state
    // object so callers can spread it into the dispatched params.
    const [advRole, setAdvRole] = useState(ADVANCED_FILTER_DEFAULTS);
    const advRoleActiveCount = useMemo(() => countActiveAdvancedFilters(advRole), [advRole]);
    const handleAdvRolePatch = (patch) => setAdvRole((prev) => ({ ...prev, ...patch }));
    const handleAdvRoleReset = () => setAdvRole(ADVANCED_FILTER_DEFAULTS);
    // Match-detail dialog state — populated when the user clicks "+N more"
    // on a tile whose matches[] was truncated.
    const [matchDetailTarget, setMatchDetailTarget] = useState(null);
    const handleShowAllMatches = (target) => setMatchDetailTarget(target);
    const closeMatchDetail = () => setMatchDetailTarget(null);

    // ---- Synergy mode state ----
    const [synergyVariant, setSynergyVariant] = useState('synergy_supporters');

    // ---- Manabase state ----
    const [manabaseProducer, setManabaseProducer] = useState('land');
    const [manabaseLandType, setManabaseLandType] = useState('');
    const [manabaseETBTapped, setManabaseETBTapped] = useState('any'); // 'any' | 'false' | 'true'

    // ---- Archetype state (cross-cutting filter) ----
    // Applies to every stats-backed tuned source via baseSourceParams (so
    // staples/synergy/manabase/combos all narrow to decks matching the
    // selected archetype). The "archetype" mode additionally requires a
    // major to be set — it uses source=archetype to surface cards
    // characteristic of that archetype path.
    //
    // Options are driven from the per-bin availability tree on the health
    // response (`availableBins[binKey].archetypes`) rather than the static
    // taxonomy — so the dropdown only ever shows combinations that have
    // actual data for the current commander+bin.
    const [archetypeParam, setArchetypeParam] = useDeepLinkParam('archetype', ARCHETYPE_ANY);
    const [subArchetypeParam, setSubArchetypeParam] = useDeepLinkParam('subArchetype', ARCHETYPE_ANY);
    const archetypesForBin = useMemo(() => {
        if (!binKey) return {};
        return availableBins[binKey]?.archetypes || {};
    }, [availableBins, binKey]);
    // Snap selection down whenever the bin changes and the previously
    // selected major/sub isn't emitted for the new bin. Empty string =
    // "not applied" (ANY).
    const archetype = useMemo(() => {
        if (!archetypeParam || archetypeParam === ARCHETYPE_ANY) return '';
        return archetypesForBin[archetypeParam] ? archetypeParam : '';
    }, [archetypeParam, archetypesForBin]);
    const subsForMajor = useMemo(() => {
        if (!archetype) return {};
        return archetypesForBin[archetype]?.subArchetypes || {};
    }, [archetypesForBin, archetype]);
    const subArchetype = useMemo(() => {
        if (!archetype) return '';
        if (!subArchetypeParam || subArchetypeParam === ARCHETYPE_ANY) return '';
        return subsForMajor[subArchetypeParam] ? subArchetypeParam : '';
    }, [archetype, subArchetypeParam, subsForMajor]);
    // Keep the URL clean: when the persisted selection snaps to ANY
    // (bin change, partial rebuild), drop the param.
    useEffect(() => {
        if (!health) return;
        const wantArch = archetype || ARCHETYPE_ANY;
        if (wantArch !== archetypeParam) setArchetypeParam(wantArch);
    }, [health, archetype, archetypeParam, setArchetypeParam]);
    useEffect(() => {
        if (!health) return;
        const wantSub = subArchetype || ARCHETYPE_ANY;
        if (wantSub !== subArchetypeParam) setSubArchetypeParam(wantSub);
    }, [health, subArchetype, subArchetypeParam, setSubArchetypeParam]);
    const setArchetype = (nextMajor) => {
        const nextKey = nextMajor || ARCHETYPE_ANY;
        setArchetypeParam(nextKey);
        const nextSubMap = nextKey === ARCHETYPE_ANY ? {} : archetypesForBin[nextKey]?.subArchetypes || {};
        if (!nextSubMap[subArchetypeParam]) setSubArchetypeParam(ARCHETYPE_ANY);
    };
    const setSubArchetype = (nextSub) => {
        setSubArchetypeParam(nextSub || ARCHETYPE_ANY);
    };

    // ---- Combo state ----
    const [comboEffects, setComboEffects] = useState(new Set());
    const [comboMaxCardCount, setComboMaxCardCount] = useState(''); // string, empty = no cap
    const [comboWinconOnly, setComboWinconOnly] = useState(false);

    // ---- Reset source state when mode changes ----
    useEffect(() => {
        if (mode === 'role') return;
        // Source modes need a fresh fetch (reset items) on mode or
        // mode-critical-param change. We don't auto-fetch here — each
        // mode's effect below decides when it's ready.
    }, [mode]);

    // ---- Shared params ----
    const selectedTypesArray = useMemo(() => [...(selectedTypes || [])], [selectedTypes]);
    const disabledColorsList = useMemo(
        () => [['W', 'U', 'B', 'R', 'G'], deck?.colors || []].reduce((p, c) => p.filter((e) => !c.includes(e))),
        [deck?.colors]
    );

    const baseSourceParams = useMemo(() => {
        const p = {
            id: deck?.id,
            commanderId,
            ...binKeyToParams(binKey),
        };
        if (colorFilters) p.colorFilters = colorFilters.toUpperCase();
        if (selectedTypesArray.length > 0 && selectedTypesArray.length !== BASE_CARD_TYPE_LIST.length) {
            p.selectedTypes = selectedTypesArray;
        }
        // Archetype filter is cross-cutting — every stats-backed source
        // accepts it (per ARCHETYPE_BINS_API_CHANGES.md). Omit when ANY
        // so cache keys stay byte-identical to the pre-feature payload.
        if (archetype) p.archetype = archetype;
        if (subArchetype) p.subArchetype = subArchetype;
        return p;
    }, [deck?.id, commanderId, binKey, colorFilters, selectedTypesArray, archetype, subArchetype]);

    // ---- Dispatching ----

    /**
     * Build the full param object for a card_category /upgrades request.
     * Folds together the primary filter bar (category/colors/types/
     * reserved-list) and the advanced filter panel (oracle text /
     * qualifiers / effect filters / etc.). `extraCategories` from the
     * advanced panel unions with the primary category — the new endpoint
     * accepts multi-value category (comma-separated).
     */
    const buildRoleParams = (overrides = {}) => {
        const id = overrides.categoryId !== undefined ? overrides.categoryId : roleCategoryId;
        const primary = roleCategoryByEnum[id]?.field;
        if (!primary) return null;
        const reservedList = overrides.reservedList !== undefined ? overrides.reservedList : reservedListSelected;
        const colors = overrides.colors !== undefined ? overrides.colors : colorFilters;
        const types = overrides.types !== undefined ? overrides.types : selectedTypes;
        const categoriesFromAdv = (advRole.extraCategories || [])
            .map((enumId) => roleCategoryByEnum[enumId]?.field)
            .filter(Boolean);
        const categories = [primary, ...categoriesFromAdv.filter((c) => c !== primary)];
        const typesArr = [...(types || [])];
        const params = {
            id: deck?.id,
            // Multi-value category: send array when >1, scalar when 1
            // (the endpoint accepts both — scalar keeps cache keys
            // byte-identical to the pre-feature payload).
            category: categories.length > 1 ? categories : categories[0],
            allowReservedList: reservedList,
        };
        if (colors) params.colorFilters = String(colors).toUpperCase();
        if (typesArr.length > 0 && typesArr.length !== BASE_CARD_TYPE_LIST.length) {
            params.selectedTypes = typesArr;
        }
        Object.assign(params, advancedFiltersToParams(advRole));
        return params;
    };

    const dispatchRoleLoad = (isReload, overrides = {}) => {
        const params = buildRoleParams(overrides);
        if (!params) return;
        if (!isReload && legacyNextCursor) params.cursor = legacyNextCursor;
        dispatch(fetchUpgradesListByCategory(params, isReload));
    };

    const handleRoleCategoryChange = (id, reservedList, colors, types) => {
        setRoleCategoryId(id);
        setReservedListSelected(reservedList);
        setSelectedTypes(types);
        dispatchRoleLoad(true, { categoryId: id, reservedList, colors, types });
    };

    const dispatchSource = (extraParams, isReload) => {
        if (!deck?.id) return;
        const params = { ...baseSourceParams, ...extraParams };
        if (!isReload && sourceCursor) {
            params.cursor = sourceCursor;
        }
        dispatch(fetchUpgrades(params, isReload));
    };

    // Auto-refetch for source modes whenever any relevant param changes.
    useEffect(() => {
        if (!deck?.id) return;

        if (mode === 'role') {
            if (!roleCategoryId) return;
            // Suppress auto-refetch while the kill switch is engaged —
            // the user sees a "search temporarily unavailable" affordance
            // until they manually retry or the operator flips the switch
            // back. Filter changes shouldn't burn requests in the
            // meantime; the doc says to respect Retry-After: 300.
            if (legacyErrorKind === 'kill_switch') return;
            dispatchRoleLoad(true);
            return;
        }

        if (mode === 'staples') {
            dispatchSource({ source: 'staples' }, true);
            return;
        }
        if (mode === 'synergy') {
            dispatchSource({ source: synergyVariant }, true);
            return;
        }
        if (mode === 'manabase') {
            const extra = { source: 'manabase' };
            if (manabaseProducer) extra.producerType = manabaseProducer;
            // landType and isETBTapped are land-only properties; only send
            // them when producer is unset or explicitly 'land', so the
            // user can't silently request contradictory criteria like
            // "producer=artifact&landType=basic".
            const landProperties = !manabaseProducer || manabaseProducer === 'land';
            if (landProperties) {
                if (manabaseLandType) extra.landType = manabaseLandType;
                if (manabaseETBTapped === 'true') extra.isETBTapped = true;
                else if (manabaseETBTapped === 'false') extra.isETBTapped = false;
            }
            // Require at least one filter beyond source=manabase (the API
            // enforces this; we mirror the rule client-side so the UI
            // doesn't surface a 400). Land-only filters only count when
            // the producer is land (or unset).
            const hasAny =
                extra.producerType ||
                (landProperties && (extra.landType || Object.prototype.hasOwnProperty.call(extra, 'isETBTapped')));
            if (!hasAny) return;
            dispatchSource(extra, true);
            return;
        }
        if (mode === 'archetype') {
            // The dedicated archetype mode needs an archetype selected to
            // even fire — without one, source=archetype has nothing to
            // anchor on. The cross-cutting filter on baseSourceParams
            // carries the actual archetype/subArchetype params.
            if (!archetype) return;
            dispatchSource({ source: 'archetype' }, true);
            return;
        }
        if (mode === 'combos') {
            const extra = { source: 'combos' };
            const effectsArr = [...comboEffects];
            if (effectsArr.length > 0) extra.effects = effectsArr.join(',');
            const parsedMax = parseInt(comboMaxCardCount, 10);
            if (Number.isFinite(parsedMax) && parsedMax > 0) extra.maxCardCount = parsedMax;
            if (comboWinconOnly) extra.winconOnly = true;
            dispatchSource(extra, true);
            return;
        }
    }, [
        mode,
        deck?.id,
        commanderId,
        binKey,
        colorFilters,
        selectedTypesArray.join(','),
        roleCategoryId,
        reservedListSelected,
        synergyVariant,
        manabaseProducer,
        manabaseLandType,
        manabaseETBTapped,
        archetype,
        subArchetype,
        [...comboEffects].join(','),
        comboMaxCardCount,
        comboWinconOnly,
        // Advanced filters drive the role mode — serialize the whole
        // object so any change kicks off a fresh fetch.
        JSON.stringify(advRole),
    ]);

    // Auto-recover from `400 invalid_cursor` (typically only happens on
    // the first page-2 request of a session whose stored cursor crosses
    // the deploy boundary). The slice has already cleared the cursor;
    // refire the request from page 1.
    useEffect(() => {
        if (mode !== 'role') return;
        if (legacyErrorKind !== 'invalid_cursor') return;
        if (!roleCategoryId) return;
        dispatchRoleLoad(true);
    }, [legacyErrorKind]);

    const handleLoadMore = () => {
        if (mode === 'role') {
            if (legacyNextCursor !== null && !legacyIsFetching) {
                dispatchRoleLoad(false);
            }
            return;
        }
        if (sourceCursor && !sourceIsFetching) {
            const extra = { source: getActiveSource() };
            // Re-send all filters along with cursor (API requires them).
            if (mode === 'synergy') extra.source = synergyVariant;
            if (mode === 'manabase') {
                if (manabaseProducer) extra.producerType = manabaseProducer;
                const landProperties = !manabaseProducer || manabaseProducer === 'land';
                if (landProperties) {
                    if (manabaseLandType) extra.landType = manabaseLandType;
                    if (manabaseETBTapped === 'true') extra.isETBTapped = true;
                    else if (manabaseETBTapped === 'false') extra.isETBTapped = false;
                }
            }
            // archetype/subArchetype ride along via baseSourceParams on
            // the load-more dispatch below — no per-mode override needed.
            if (mode === 'combos') {
                const effectsArr = [...comboEffects];
                if (effectsArr.length > 0) extra.effects = effectsArr.join(',');
                const parsedMax = parseInt(comboMaxCardCount, 10);
                if (Number.isFinite(parsedMax) && parsedMax > 0) extra.maxCardCount = parsedMax;
                if (comboWinconOnly) extra.winconOnly = true;
            }
            dispatchSource(extra, false);
        }
    };

    const getActiveSource = () => {
        if (mode === 'staples') return 'staples';
        if (mode === 'synergy') return synergyVariant;
        if (mode === 'manabase') return 'manabase';
        if (mode === 'archetype') return 'archetype';
        if (mode === 'combos') return 'combos';
        return null;
    };

    // ---- Color filter onChange ----
    const handleColorFiltersChange = useDebouncedCallback((evn) => {
        const nextColors = evn?.colors ? evn.colors.toUpperCase() : null;
        setColorFilters(nextColors || null);
        if (mode === 'role') {
            handleRoleCategoryChange(
                roleCategoryId,
                reservedListSelected,
                evn?.colors?.toUpperCase() || null,
                selectedTypes
            );
        }
    }, 300);

    const handleTypesChange = (evn) => {
        setSelectedTypes(evn);
        if (mode === 'role') {
            handleRoleCategoryChange(roleCategoryId, reservedListSelected, colorFilters, evn);
        }
    };

    // ---- Mode-specific setup ----

    const roleCategoryList = getRoleCategoryList();
    const typesLabel = (() => {
        const count = selectedTypesArray.length;
        if (count === BASE_CARD_TYPE_LIST.length) return 'All types';
        if (count === 0) return 'No types';
        return `${count} of ${BASE_CARD_TYPE_LIST.length} types`;
    })();

    const activeCategoryLabel = ROLE_CATEGORY_CONSTANTS[roleCategoryId]?.label || null;

    // ---- Renderers ----

    const renderFilterBanner = () => {
        if (mode === 'role') {
            if (!legacyHasDoneFirstLoad || !activeCategoryLabel) return null;
            // Prefer the API's Total when present (additive field on the
            // new contract — exact-count by default). Fall back to the
            // legacy "N+ picks" estimate when the response predates the
            // base-search overhaul.
            const detail = (() => {
                if (legacyTotal > 0) return `${filteredLegacyList.length} of ${legacyTotal} picks`;
                if (legacyList.length > 0) {
                    return `${filteredLegacyList.length}${legacyNextCursor !== null ? '+' : ''} picks`;
                }
                return null;
            })();
            return (
                <FilterBanner
                    label="Suggesting"
                    value={activeCategoryLabel}
                    detail={detail}
                    onClear={() => setRoleCategoryId(null)}
                    ariaLabel="Clear category"
                />
            );
        }
        if (!sourceHasDoneFirstLoad) return null;
        const mainLabel = (() => {
            if (mode === 'staples') return `${activeBinLabel} · Staples`;
            if (mode === 'synergy') {
                const variant = SYNERGY_VARIANTS.find((v) => v.id === synergyVariant);
                return `${activeBinLabel} · ${variant?.label || synergyVariant}`;
            }
            if (mode === 'manabase') {
                const parts = [];
                if (manabaseProducer) parts.push(MANABASE_PRODUCER_TYPES.find((p) => p.id === manabaseProducer)?.label);
                const landProperties = !manabaseProducer || manabaseProducer === 'land';
                if (landProperties) {
                    if (manabaseLandType) parts.push(MANABASE_LAND_TYPES.find((p) => p.id === manabaseLandType)?.label);
                    if (manabaseETBTapped === 'false') parts.push('Untapped');
                    if (manabaseETBTapped === 'true') parts.push('Enters tapped');
                }
                const label = parts.filter(Boolean).join(' · ') || 'Manabase';
                return `${activeBinLabel} · ${label}`;
            }
            if (mode === 'archetype') {
                const majorLabel = archetype ? prettyStatsMajor(archetype) : '—';
                const subLabel = subArchetype ? ` / ${prettyMinor(subArchetype)}` : '';
                return `${activeBinLabel} · ${majorLabel}${subLabel}`;
            }
            if (mode === 'combos') {
                const parts = [];
                if (comboWinconOnly) parts.push('Wincons');
                if (comboMaxCardCount) parts.push(`≤${comboMaxCardCount} cards`);
                if (comboEffects.size > 0) parts.push(`${comboEffects.size} effects`);
                const label = parts.length > 0 ? parts.join(' · ') : 'All combos';
                return `${activeBinLabel} · ${label}`;
            }
            return activeBinLabel;
        })();
        const detail = sourceTotal > 0 ? `${sourceItems.length} of ${sourceTotal} picks` : null;
        return (
            <FilterBanner
                label="Suggesting"
                value={mainLabel}
                detail={detail}
                onClear={() => setMode('role')}
                ariaLabel="Back to role-based upgrades"
            />
        );
    };

    const renderLegacyError = () => {
        if (!legacyError) return null;
        if (legacyErrorKind === 'invalid_cursor') {
            // Auto-recovered by the effect above — render nothing while
            // the refire is in flight to avoid flashing an error.
            return null;
        }
        if (legacyErrorKind === 'kill_switch') {
            const seconds = legacyRetryAfter ?? 300;
            return (
                <div className="upgradelizerEmpty upgradelizerEmpty--compact">
                    <div className="upgradelizerEmptyTitle">Search temporarily unavailable</div>
                    <div className="upgradelizerEmptySubtitle">
                        Upgrade search is paused for maintenance. Try again in about {Math.round(seconds / 60)} minute
                        {seconds >= 120 ? 's' : ''}.
                    </div>
                    <button
                        type="button"
                        className="upgradelizerLoadMore"
                        onClick={() => dispatchRoleLoad(true)}
                        style={{ marginTop: 12 }}
                    >
                        Retry now
                    </button>
                </div>
            );
        }
        if (legacyErrorKind === 'timeout') {
            return (
                <div className="upgradelizerEmpty upgradelizerEmpty--compact">
                    <div className="upgradelizerEmptyTitle">Search timed out</div>
                    <div className="upgradelizerEmptySubtitle">
                        The query took too long. Try a tighter filter, or retry.
                    </div>
                    <button
                        type="button"
                        className="upgradelizerLoadMore"
                        onClick={() => dispatchRoleLoad(true)}
                        style={{ marginTop: 12 }}
                    >
                        Retry
                    </button>
                </div>
            );
        }
        return (
            <div className="upgradelizerEmpty upgradelizerEmpty--compact">
                <div className="upgradelizerEmptyTitle">Request failed</div>
                <div className="upgradelizerEmptySubtitle">{legacyError}</div>
            </div>
        );
    };

    const renderMainResults = () => {
        if (mode === 'role') {
            // Kill switch / timeout / generic error short-circuit the
            // result grid. invalid_cursor is auto-recovered silently.
            if (legacyError && legacyErrorKind !== 'invalid_cursor') {
                return renderLegacyError();
            }
            if (!legacyHasDoneFirstLoad) {
                return (
                    <div className="upgradelizerEmpty">
                        {legacyIsFetching ? (
                            <SearedLoader size="large" subcaption="Pulling upgrade candidates…" />
                        ) : (
                            <>
                                <div className="upgradelizerEmptyArrow" aria-hidden="true">
                                    ↑
                                </div>
                                <div className="upgradelizerEmptyTitle">Pick a category to begin</div>
                                <div className="upgradelizerEmptySubtitle">
                                    Counterspells, Tutors, Fast Mana, Ramp, Board Wipes, Wincons — choose a role and the
                                    upgrader surfaces cards the deck is missing from that bucket.
                                </div>
                            </>
                        )}
                    </div>
                );
            }
            if (filteredLegacyList.length === 0) {
                // A filter change kicks off an isReload fetch that wipes
                // the list before the response lands — show the loader
                // instead of the "no picks" empty state when we're
                // actively waiting on a response.
                if (legacyIsFetching) {
                    return (
                        <div className="upgradelizerEmpty">
                            <SearedLoader size="large" subcaption="Pulling upgrade candidates…" />
                        </div>
                    );
                }
                return (
                    <div className="upgradelizerEmpty upgradelizerEmpty--compact">
                        <div className="upgradelizerEmptyTitle">No picks found</div>
                        <div className="upgradelizerEmptySubtitle">
                            Loosen the type or color filters, or toggle Reserved List on, to widen the search.
                        </div>
                    </div>
                );
            }
            return (
                <div className="upgradelizerGrid">
                    {filteredLegacyList.map((item) => (
                        <UpgradelizerCardTile
                            key={item.id}
                            item={{ ...item, cardId: item.cardId || item.id }}
                            onSelectCard={openCardInfo}
                            onShowAllMatches={UPGRADES_BASE_SEARCH_OVERHAUL_ENABLED ? handleShowAllMatches : undefined}
                            priceInfo={priceByName[item?.name?.toLowerCase()]}
                        />
                    ))}
                </div>
            );
        }

        // Source modes.
        if (sourceError) {
            return (
                <div className="upgradelizerEmpty upgradelizerEmpty--compact">
                    <div className="upgradelizerEmptyTitle">Request failed</div>
                    <div className="upgradelizerEmptySubtitle">{sourceError}</div>
                </div>
            );
        }
        if (!sourceHasDoneFirstLoad) {
            if (sourceIsFetching) {
                return (
                    <div className="upgradelizerEmpty">
                        <SearedLoader size="large" subcaption="Crunching commander stats…" />
                    </div>
                );
            }
            // Mode needs a required filter before it can fire.
            return renderSourceEmptyPrompt();
        }
        if (sourceStatus === 'pending') {
            return (
                <div className="upgradelizerEmpty">
                    <div className="upgradelizerEmptyArrow" aria-hidden="true">
                        ⟳
                    </div>
                    <div className="upgradelizerEmptyTitle">Gathering commander stats…</div>
                    <div className="upgradelizerEmptySubtitle">
                        This commander&apos;s aggregate stats are still being computed. Try another bin or check back in
                        a few minutes.
                    </div>
                </div>
            );
        }
        if (sourceNote) {
            return (
                <div className="upgradelizerEmpty upgradelizerEmpty--compact">
                    <div className="upgradelizerEmptyTitle">No cards match</div>
                    <div className="upgradelizerEmptySubtitle">
                        Try a different bracket, archetype, or role — no rows were returned for this selection (
                        {sourceNote.replace(/_/g, ' ')}).
                    </div>
                </div>
            );
        }
        if (sourceItems.length === 0) {
            // Filter change kicked off an isReload — show the loader
            // instead of "no picks" while the response is in flight.
            if (sourceIsFetching) {
                return (
                    <div className="upgradelizerEmpty">
                        <SearedLoader size="large" subcaption="Crunching commander stats…" />
                    </div>
                );
            }
            return (
                <div className="upgradelizerEmpty upgradelizerEmpty--compact">
                    <div className="upgradelizerEmptyTitle">No picks for this selection</div>
                    <div className="upgradelizerEmptySubtitle">
                        Widen the color / type filter, or try another bracket.
                    </div>
                </div>
            );
        }

        // Pick the right renderer based on the returned source.
        if (mode === 'combos' || sourceLastSource === 'combos') {
            return (
                <div className="upgradelizerComboList">
                    {sourceItems.map((item) => (
                        <UpgradelizerComboRow
                            key={item.comboId || item.key}
                            item={item}
                            onSelectCard={openCardInfo}
                            priceByName={priceByName}
                        />
                    ))}
                </div>
            );
        }
        if (synergyVariant === 'top_synergies' && mode === 'synergy') {
            return (
                <div className="upgradelizerSynergyList">
                    {sourceItems.map((item) => (
                        <UpgradelizerSynergyRow
                            key={item.key || item.anchorCard?.cardId}
                            item={item}
                            onSelectCard={openCardInfo}
                        />
                    ))}
                </div>
            );
        }
        return (
            <div className="upgradelizerGrid">
                {sourceItems.map((item) => {
                    let badge = null;
                    if (item?.supporterCount != null) {
                        badge = `${item.supporterCount} syn`;
                    } else if (item?.anchor?.deckCount != null) {
                        badge = `${item.anchor.deckCount} decks`;
                    }
                    return (
                        <UpgradelizerCardTile
                            key={item.cardId || item.key}
                            item={item}
                            onSelectCard={openCardInfo}
                            showUsage
                            badge={badge}
                            priceInfo={priceByName[item?.name?.toLowerCase()]}
                        />
                    );
                })}
            </div>
        );
    };

    const renderSourceEmptyPrompt = () => {
        if (mode === 'manabase') {
            return (
                <div className="upgradelizerEmpty upgradelizerEmpty--compact">
                    <div className="upgradelizerEmptyTitle">Pick a manabase filter</div>
                    <div className="upgradelizerEmptySubtitle">
                        Select a producer type, land type, or toggle fast-mana / fetch / untapped to see candidates.
                    </div>
                </div>
            );
        }
        if (mode === 'archetype') {
            return (
                <div className="upgradelizerEmpty upgradelizerEmpty--compact">
                    <div className="upgradelizerEmptyTitle">Pick a major archetype</div>
                    <div className="upgradelizerEmptySubtitle">
                        Choose a major (Midrange / Control / Combo) and optionally narrow to a minor sub-archetype to
                        surface cards that appear in that archetype path for this commander.
                    </div>
                </div>
            );
        }
        return null;
    };

    const showLoadMore = (() => {
        if (mode === 'role') return legacyNextCursor !== null && filteredLegacyList.length > 0;
        return sourceCursor != null && sourceItems.length > 0;
    })();

    const loadMoreBusy = mode === 'role' ? legacyIsFetching : sourceIsFetching;

    // ---- Render ----

    return (
        <Flex direction="column" UNSAFE_className="resultsPane baseDeckPaneWrap">
            <div className="panelGradient baseDeckPaneListPanel upgradelizerPanel">
                <Flex direction="column" rowGap={14}>
                    {(() => {
                        const tunedOn = tunedAvailable === true;
                        const heroTier = tunedOn ? 'tuned' : tunedAvailable === false ? 'generic' : 'standard';
                        const heroName = tunedOn
                            ? `Tuned for ${commanderDisplay}`
                            : tunedAvailable === false
                            ? 'Generic Upgrade Paths'
                            : 'Upgrade Finder';
                        const totalTunedBins = (availableBinSections || []).reduce(
                            (acc, s) => acc + (s?.children?.length || 0),
                            0
                        );
                        const statValue = tunedOn
                            ? totalTunedBins
                            : tunedAvailable === false
                            ? GENERIC_MODES.length
                            : '—';
                        const statLabel = tunedOn
                            ? 'Tuning bins'
                            : tunedAvailable === false
                            ? 'Generic paths'
                            : 'Checking';
                        const statSub = tunedOn
                            ? totalTunedBins === 1
                                ? 'available'
                                : 'available'
                            : tunedAvailable === false
                            ? 'available'
                            : 'commander stats';
                        return (
                            <HeroPanel
                                className="upgradelizerHero"
                                data-tier={heroTier}
                                eyebrow="Upgrade Finder"
                                title={heroName}
                                hint={
                                    tunedAvailable === false ? (
                                        `Commander-tuned upgrades aren't available for ${commanderDisplay} yet — not enough decks indexed. Generic role-based suggestions still work.`
                                    ) : (
                                        <>
                                            Two flavors of suggestions: <strong>generic</strong> roles that apply to any
                                            deck, or upgrades <strong>tuned to {commanderDisplay}</strong> pulled from
                                            aggregate stats at the selected bracket or power level.
                                        </>
                                    )
                                }
                                aside={<HeroPanelTotal label={statLabel} value={statValue} sub={statSub} />}
                            />
                        );
                    })()}

                    {/* Mode tab groups — split into generic vs commander-tuned so
                        the distinction is unmissable. */}
                    <div className="upgradelizerModeGroups">
                        <div className="upgradelizerModeGroup upgradelizerModeGroup--generic">
                            <span className="upgradelizerModeGroupLabel">
                                <span className="upgradelizerModeGroupKicker">Generic</span>
                                <span className="upgradelizerModeGroupCaption">Any deck · any commander</span>
                            </span>
                            <div className="upgradelizerModeSwitch" role="tablist" aria-label="Generic upgrades">
                                {GENERIC_MODES.map((m) => renderModeTab(m, mode, setMode))}
                            </div>
                        </div>
                        <div
                            className={`upgradelizerModeGroup upgradelizerModeGroup--tuned${
                                tunedAvailable === false ? ' upgradelizerModeGroup--disabled' : ''
                            }`}
                            aria-disabled={tunedAvailable === false}
                        >
                            <span className="upgradelizerModeGroupLabel">
                                <span className="upgradelizerModeGroupKicker">Tuned</span>
                                <span className="upgradelizerModeGroupCaption">
                                    {renderTunedCaption({
                                        health,
                                        healthFetching,
                                        tunedAvailable,
                                        availableBinSections,
                                    })}
                                </span>
                            </span>
                            <div
                                className="upgradelizerModeSwitch"
                                role="tablist"
                                aria-label={`Commander-tuned upgrades for ${commanderDisplay}`}
                            >
                                {TUNED_MODES.map((m) => renderModeTab(m, mode, setMode, tunedAvailable === false))}
                            </div>
                        </div>
                    </div>

                    {/* Primary filter bar */}
                    <div className={`upgradelizerFilters${mobile ? '' : ' upgradelizerFiltersSticky'}`}>
                        <Flex direction="row" wrap columnGap={16} rowGap={12} alignItems="end">
                            {mode === 'role' ? (
                                <RoleModeControls
                                    roleCategoryList={roleCategoryList}
                                    roleCategoryId={roleCategoryId}
                                    activeCategoryLabel={activeCategoryLabel}
                                    isFetching={legacyIsFetching}
                                    onCategoryChange={(id) =>
                                        handleRoleCategoryChange(id, reservedListSelected, colorFilters, selectedTypes)
                                    }
                                />
                            ) : (
                                <BinControl
                                    binKey={binKey}
                                    onChange={setBinKey}
                                    sections={availableBinSections}
                                    isDisabled={tunedAvailable === false}
                                />
                            )}
                            {mode === 'synergy' && (
                                <SynergyVariantControl variant={synergyVariant} onChange={setSynergyVariant} />
                            )}
                            {mode === 'manabase' && (
                                <ManabaseControls
                                    producer={manabaseProducer}
                                    onProducerChange={setManabaseProducer}
                                    landType={manabaseLandType}
                                    onLandTypeChange={setManabaseLandType}
                                    etbTapped={manabaseETBTapped}
                                    onEtbTappedChange={setManabaseETBTapped}
                                />
                            )}
                            {mode !== 'role' && (
                                <ArchetypeControls
                                    major={archetype}
                                    onMajorChange={setArchetype}
                                    minor={subArchetype}
                                    onMinorChange={setSubArchetype}
                                    required={mode === 'archetype'}
                                    majorOptions={archetypesForBin}
                                    subOptions={subsForMajor}
                                />
                            )}
                            {mode === 'combos' && (
                                <ComboControls
                                    effects={comboEffects}
                                    onEffectsChange={setComboEffects}
                                    maxCardCount={comboMaxCardCount}
                                    onMaxCardCountChange={setComboMaxCardCount}
                                    winconOnly={comboWinconOnly}
                                    onWinconOnlyChange={setComboWinconOnly}
                                />
                            )}
                            {mode !== 'combos' && mode !== 'manabase' && (
                                <TypesControl
                                    typesLabel={typesLabel}
                                    selectedTypes={selectedTypes}
                                    onChange={handleTypesChange}
                                    isDisabled={mode === 'role' && (!legacyHasDoneFirstLoad || legacyIsFetching)}
                                />
                            )}
                            <div className="upgradelizerControl upgradelizerControl--colors">
                                <span className="upgradelizerControlLabel">Color Identity</span>
                                <ManaSelector
                                    handleColorFilterChange={handleColorFiltersChange}
                                    // ManaSelector compares with lowercase
                                    // (`.includes('w')`). The state holds
                                    // uppercase because the API param is
                                    // uppercase — normalize here so every
                                    // selected symbol stays visually
                                    // selected. Don't blank during fetch:
                                    // emptying mid-click loses the user's
                                    // other colors when the selector
                                    // rebuilds the identity from props.
                                    colorFilters={(
                                        colorFilters ||
                                        deck?.colorIdentity?.toLowerCase() ||
                                        ''
                                    ).toLowerCase()}
                                    disabledColors={disabledColorsList}
                                />
                            </div>
                            {mode === 'role' && (
                                <div className="upgradelizerControl upgradelizerControl--reserved">
                                    <Checkbox
                                        isSelected={reservedListSelected}
                                        onChange={(evn) =>
                                            handleRoleCategoryChange(roleCategoryId, evn, colorFilters, selectedTypes)
                                        }
                                        isDisabled={!legacyHasDoneFirstLoad || legacyIsFetching}
                                    >
                                        <span className="upgradelizerReservedLabel">Include Reserved List</span>
                                    </Checkbox>
                                </div>
                            )}
                        </Flex>
                    </div>

                    {mode === 'role' && UPGRADES_BASE_SEARCH_OVERHAUL_ENABLED && (
                        <UpgradelizerAdvancedFilters
                            value={advRole}
                            onChange={handleAdvRolePatch}
                            onReset={handleAdvRoleReset}
                            activeCount={advRoleActiveCount}
                            disabled={!roleCategoryId}
                            disabledHint="Pick a category to enable"
                            extraCategoryOptions={Object.values(ROLE_CATEGORY_CONSTANTS)
                                .filter((c) => c.enum !== roleCategoryId)
                                .map((c) => ({ id: c.enum, label: c.label }))}
                        />
                    )}
                    {renderFilterBanner()}
                    {renderMainResults()}
                    {showLoadMore && (
                        <Flex direction="row" justifyContent="center" marginTop={12} marginBottom={4}>
                            <button
                                type="button"
                                className="upgradelizerLoadMore"
                                onClick={handleLoadMore}
                                disabled={loadMoreBusy}
                                aria-label="Load more picks"
                            >
                                {loadMoreBusy ? (
                                    <ProgressCircle isIndeterminate size="S" aria-label="Loading" />
                                ) : (
                                    <>
                                        <span>Show more picks</span>
                                        <DownloadFromCloud size="XS" />
                                    </>
                                )}
                            </button>
                        </Flex>
                    )}
                </Flex>
            </div>
            {matchDetailTarget && UPGRADES_BASE_SEARCH_OVERHAUL_ENABLED && (
                <UpgradelizerMatchDetailDialog
                    cardId={matchDetailTarget.cardId}
                    category={matchDetailTarget.category}
                    cardName={matchDetailTarget.cardName}
                    onClose={closeMatchDetail}
                />
            )}
        </Flex>
    );
}

// ---- Sub-controls ----

function BinControl({ binKey, onChange, sections, isDisabled }) {
    const hasAny = (sections || []).some((s) => s.children.length > 0);
    const activeDeckCount = (() => {
        for (const section of sections || []) {
            const match = section.children.find((c) => c.key === binKey);
            if (match) return match.deckCount;
        }
        return null;
    })();
    return (
        <div className="upgradelizerControl upgradelizerControl--category">
            <span className="upgradelizerControlLabel">Bin</span>
            <div className="upgradelizerBinRow">
                <Picker
                    aria-label="Show upgrades for bracket or power level"
                    selectedKey={binKey}
                    onSelectionChange={(key) => {
                        if (key) onChange(String(key));
                    }}
                    items={sections || []}
                    UNSAFE_className="upgradelizerBinPicker"
                    width={180}
                    isDisabled={isDisabled || !hasAny}
                >
                    {(section) => (
                        <Section key={section.id} items={section.children} title={section.name}>
                            {(option) => (
                                <Item key={option.key} textValue={option.label}>
                                    {option.label}
                                </Item>
                            )}
                        </Section>
                    )}
                </Picker>
                {activeDeckCount != null && (
                    <span
                        className="upgradelizerBinCount"
                        title={`${activeDeckCount.toLocaleString()} decks in this bin`}
                    >
                        {activeDeckCount.toLocaleString()} <span className="upgradelizerBinCountUnit">decks</span>
                    </span>
                )}
            </div>
        </div>
    );
}

function RoleModeControls({ roleCategoryList, roleCategoryId, activeCategoryLabel, isFetching, onCategoryChange }) {
    return (
        <div className="upgradelizerControl upgradelizerControl--category">
            <span className="upgradelizerControlLabel">Category</span>
            <MenuTrigger>
                <ActionButton isDisabled={isFetching} UNSAFE_className="upgradelizerPickerBtn">
                    <span className="upgradelizerPickerValue">{activeCategoryLabel || 'Select a category…'}</span>
                    <ChevronDown size="XS" />
                </ActionButton>
                <Menu
                    items={roleCategoryList}
                    selectedKeys={roleCategoryId}
                    onSelectionChange={(evn) => onCategoryChange(evn.currentKey)}
                    selectionMode="single"
                >
                    {(item) => (
                        <Section items={item.children} title={item.name}>
                            {(child) => <Item>{child.name}</Item>}
                        </Section>
                    )}
                </Menu>
            </MenuTrigger>
        </div>
    );
}

function SynergyVariantControl({ variant, onChange }) {
    const active = SYNERGY_VARIANTS.find((v) => v.id === variant) || SYNERGY_VARIANTS[0];
    return (
        <div className="upgradelizerControl">
            <span className="upgradelizerControlLabel">Synergy lens</span>
            <MenuTrigger>
                <ActionButton UNSAFE_className="upgradelizerPickerBtn">
                    <span className="upgradelizerPickerValue">{active.label}</span>
                    <ChevronDown size="XS" />
                </ActionButton>
                <Menu
                    items={SYNERGY_VARIANTS}
                    selectedKeys={new Set([variant])}
                    onSelectionChange={(evn) => onChange(evn.currentKey)}
                    selectionMode="single"
                >
                    {(item) => <Item key={item.id}>{item.label}</Item>}
                </Menu>
            </MenuTrigger>
        </div>
    );
}

function ManabaseControls({ producer, onProducerChange, landType, onLandTypeChange, etbTapped, onEtbTappedChange }) {
    const activeProducer = MANABASE_PRODUCER_TYPES.find((p) => p.id === producer) || MANABASE_PRODUCER_TYPES[0];
    const activeLand = MANABASE_LAND_TYPES.find((p) => p.id === landType) || MANABASE_LAND_TYPES[0];
    const etbLabel = etbTapped === 'false' ? 'Untapped only' : etbTapped === 'true' ? 'Tapped only' : 'Either';
    // Land type + enters-tapped are land-only properties — only show them
    // when the user has narrowed producer to land (or left it unset, in
    // which case "Any producer" still includes lands). Any non-land
    // producer (artifact/creature/etc.) makes these filters meaningless,
    // so we hide them rather than let the user silently request
    // contradictory criteria.
    const showLandOnly = !producer || producer === 'land';
    return (
        <>
            <div className="upgradelizerControl">
                <span className="upgradelizerControlLabel">Producer</span>
                <MenuTrigger>
                    <ActionButton UNSAFE_className="upgradelizerPickerBtn">
                        <span className="upgradelizerPickerValue">{activeProducer.label}</span>
                        <ChevronDown size="XS" />
                    </ActionButton>
                    <Menu
                        items={MANABASE_PRODUCER_TYPES}
                        selectedKeys={new Set([producer || MANABASE_ANY_KEY])}
                        onSelectionChange={(evn) =>
                            onProducerChange(evn.currentKey === MANABASE_ANY_KEY ? '' : evn.currentKey || '')
                        }
                        selectionMode="single"
                    >
                        {(item) => <Item key={item.id || MANABASE_ANY_KEY}>{item.label}</Item>}
                    </Menu>
                </MenuTrigger>
            </div>
            {showLandOnly && (
                <div className="upgradelizerControl">
                    <span className="upgradelizerControlLabel">Land type</span>
                    <MenuTrigger>
                        <ActionButton UNSAFE_className="upgradelizerPickerBtn">
                            <span className="upgradelizerPickerValue">{activeLand.label}</span>
                            <ChevronDown size="XS" />
                        </ActionButton>
                        <Menu
                            items={MANABASE_LAND_TYPES}
                            selectedKeys={new Set([landType || MANABASE_ANY_KEY])}
                            onSelectionChange={(evn) =>
                                onLandTypeChange(evn.currentKey === MANABASE_ANY_KEY ? '' : evn.currentKey || '')
                            }
                            selectionMode="single"
                        >
                            {(item) => <Item key={item.id || MANABASE_ANY_KEY}>{item.label}</Item>}
                        </Menu>
                    </MenuTrigger>
                </div>
            )}
            {showLandOnly && (
                <div className="upgradelizerControl">
                    <span className="upgradelizerControlLabel">Enters tapped</span>
                    <MenuTrigger>
                        <ActionButton UNSAFE_className="upgradelizerPickerBtn">
                            <span className="upgradelizerPickerValue">{etbLabel}</span>
                            <ChevronDown size="XS" />
                        </ActionButton>
                        <Menu
                            selectedKeys={new Set([etbTapped])}
                            onSelectionChange={(evn) => onEtbTappedChange(evn.currentKey || 'any')}
                            selectionMode="single"
                        >
                            <Item key="any">Either</Item>
                            <Item key="false">Untapped only</Item>
                            <Item key="true">Tapped only</Item>
                        </Menu>
                    </MenuTrigger>
                </div>
            )}
        </>
    );
}

const ARCHETYPE_MINOR_ANY_KEY = '__any_minor__';
const ARCHETYPE_MAJOR_ANY_KEY = '__any_major__';

function ArchetypeControls({
    major,
    onMajorChange,
    minor,
    onMinorChange,
    required = false,
    majorOptions = {},
    subOptions = {},
}) {
    // majorOptions / subOptions come from the per-bin availability tree on
    // the health response: `{ [MAJOR]: { deckCount, subArchetypes: {...} } }`
    // and `{ [SUB]: { deckCount, ... } }` respectively. We only show
    // combinations the API will actually return data for.
    const majorEntries = useMemo(
        () =>
            Object.entries(majorOptions)
                .filter(([, info]) => info && (info.deckCount ?? 0) > 0)
                .sort((a, b) => (b[1].deckCount || 0) - (a[1].deckCount || 0))
                .map(([id, info]) => ({ id, label: prettyStatsMajor(id), deckCount: info.deckCount || 0 })),
        [majorOptions]
    );
    const subEntries = useMemo(
        () =>
            Object.entries(subOptions)
                .filter(([, info]) => info && (info.deckCount ?? 0) > 0)
                .sort((a, b) => (b[1].deckCount || 0) - (a[1].deckCount || 0))
                .map(([id, info]) => ({ id, label: prettyMinor(id), deckCount: info.deckCount || 0 })),
        [subOptions]
    );
    const activeMajor = majorEntries.find((m) => m.id === major);
    const majorPlaceholder = required ? 'Pick an archetype…' : 'Any archetype';
    const majorLabel = activeMajor ? `${activeMajor.label} (${fmtBinCount(activeMajor.deckCount)})` : majorPlaceholder;
    const activeSub = subEntries.find((s) => s.id === minor);
    const subLabel = activeSub ? `${activeSub.label} (${fmtBinCount(activeSub.deckCount)})` : 'Any sub';
    const majorEmpty = majorEntries.length === 0;
    const subEmpty = subEntries.length === 0;
    return (
        <>
            <div className="upgradelizerControl">
                <span className="upgradelizerControlLabel">Archetype{required ? '' : ' (optional)'}</span>
                <MenuTrigger>
                    <ActionButton
                        UNSAFE_className="upgradelizerPickerBtn"
                        isDisabled={majorEmpty}
                        title={majorEmpty ? 'No archetype data for this bin yet.' : undefined}
                    >
                        <span className="upgradelizerPickerValue">{majorEmpty ? 'No data yet' : majorLabel}</span>
                        <ChevronDown size="XS" />
                    </ActionButton>
                    <Menu
                        selectedKeys={new Set([major || ARCHETYPE_MAJOR_ANY_KEY])}
                        onSelectionChange={(evn) =>
                            onMajorChange(evn.currentKey === ARCHETYPE_MAJOR_ANY_KEY ? '' : evn.currentKey || '')
                        }
                        selectionMode="single"
                    >
                        {required ? null : <Item key={ARCHETYPE_MAJOR_ANY_KEY}>Any archetype</Item>}
                        {majorEntries.map((entry) => (
                            <Item key={entry.id}>{`${entry.label} (${fmtBinCount(entry.deckCount)})`}</Item>
                        ))}
                    </Menu>
                </MenuTrigger>
            </div>
            <div className="upgradelizerControl">
                <span className="upgradelizerControlLabel">Sub-archetype</span>
                <MenuTrigger>
                    <ActionButton
                        UNSAFE_className="upgradelizerPickerBtn"
                        isDisabled={!major || subEmpty}
                        title={
                            !major
                                ? 'Pick an archetype first'
                                : subEmpty
                                ? 'No sub-archetype data for this combination.'
                                : undefined
                        }
                    >
                        <span className="upgradelizerPickerValue">
                            {!major ? 'Pick an archetype first' : subEmpty ? 'No data yet' : subLabel}
                        </span>
                        <ChevronDown size="XS" />
                    </ActionButton>
                    <Menu
                        selectedKeys={new Set([minor || ARCHETYPE_MINOR_ANY_KEY])}
                        onSelectionChange={(evn) =>
                            onMinorChange(evn.currentKey === ARCHETYPE_MINOR_ANY_KEY ? '' : evn.currentKey || '')
                        }
                        selectionMode="single"
                    >
                        <Item key={ARCHETYPE_MINOR_ANY_KEY}>Any sub</Item>
                        {subEntries.map((entry) => (
                            <Item key={entry.id}>{`${entry.label} (${fmtBinCount(entry.deckCount)})`}</Item>
                        ))}
                    </Menu>
                </MenuTrigger>
            </div>
        </>
    );
}

function ComboControls({
    effects,
    onEffectsChange,
    maxCardCount,
    onMaxCardCountChange,
    winconOnly,
    onWinconOnlyChange,
}) {
    const effectsLabel =
        effects.size === 0
            ? 'Any effect'
            : effects.size === 1
            ? [...effects][0].replace('-', ' ')
            : `${effects.size} effects`;
    return (
        <>
            <div className="upgradelizerControl">
                <span className="upgradelizerControlLabel">Effects</span>
                <MenuTrigger>
                    <ActionButton UNSAFE_className="upgradelizerPickerBtn">
                        <span className="upgradelizerPickerValue">{effectsLabel}</span>
                        <ChevronDown size="XS" />
                    </ActionButton>
                    <Menu
                        items={COMBO_EFFECTS}
                        selectedKeys={effects}
                        onSelectionChange={(set) => onEffectsChange(set)}
                        selectionMode="multiple"
                    >
                        {(item) => <Item key={item.id}>{item.label}</Item>}
                    </Menu>
                </MenuTrigger>
            </div>
            <div className="upgradelizerControl upgradelizerControl--narrow">
                <span className="upgradelizerControlLabel">Max cards</span>
                <input
                    type="number"
                    min={1}
                    max={10}
                    className="upgradelizerTextInput"
                    value={maxCardCount}
                    onChange={(evn) => onMaxCardCountChange(evn.target.value.replace(/[^0-9]/g, ''))}
                    placeholder="any"
                />
            </div>
            <div className="upgradelizerControl upgradelizerControl--reserved">
                <Checkbox isSelected={winconOnly} onChange={onWinconOnlyChange}>
                    <span className="upgradelizerReservedLabel">Wincons only</span>
                </Checkbox>
            </div>
        </>
    );
}

function TypesControl({ typesLabel, selectedTypes, onChange, isDisabled }) {
    return (
        <div className="upgradelizerControl">
            <span className="upgradelizerControlLabel">Types</span>
            <MenuTrigger>
                <ActionButton isDisabled={isDisabled} UNSAFE_className="upgradelizerPickerBtn">
                    <span className="upgradelizerPickerValue">{typesLabel}</span>
                    <ChevronDown size="XS" />
                </ActionButton>
                <Menu
                    items={commonCardTypeList}
                    selectedKeys={selectedTypes}
                    onSelectionChange={(evn) => onChange(evn)}
                    selectionMode="multiple"
                >
                    {(item) => <Item>{item.name}</Item>}
                </Menu>
            </MenuTrigger>
        </div>
    );
}
